/* The hero figure, as the page's one React island.

   On the Astro site the whole home page was React: `index.astro` handed
   everything to `<Site client:load>`, which server-rendered the copy and then
   hydrated all of it. Under WordPress the split moved. PHP renders the hero
   copy, the steps, the access form and the FAQ, so that text is in the HTML
   with no hydration at all, and React is left with the one thing it is
   actually needed for: two sheets animated through a compare -- overlaid,
   every shape framed, pulled apart, one shape zoomed in on in both.

   RevisionAnimation.jsx is ported from the "Revision Figure animated" design
   bundle. It replaced RevisionShapes.jsx, the click-to-compare figure, which
   is in git history if it is wanted back. figures.jsx is generated data --
   shape boxes read off the overlay SVGs -- and rewriting it by hand is how a
   frame stops sitting on its shape.

   Everything this file adds is the plumbing those two do not get for
   themselves: where the SVGs live, what the copy says, where to mount, and
   the end card's link. */
import React from "react";
import { createRoot } from "react-dom/client";

import { figure } from "./figures.jsx";
import { RevisionAnimation } from "./RevisionAnimation.jsx";
import { StringsProvider, injectedStrings } from "./strings-context.jsx";

/** The element PHP leaves for the island, and the data it prints beside it. */
const MOUNT_ID = "cd-figure";

/**
 * Point the figure's four SVG paths at the theme.
 *
 * figures.jsx names them `/figures/revision-c.svg`, which is where they sat in
 * the Astro site's `public/`. In WordPress they are theme assets under a URL
 * nothing can know at build time, so the base arrives with the copy and the
 * swap happens here rather than in the data.
 */
function withBase(option, base) {
	const at = (path) => base.replace(/\/$/, "") + path.replace(/^\/figures/, "");
	return {
		...option,
		a: at(option.a),
		b: at(option.b),
		aChanges: at(option.aChanges),
		bChanges: at(option.bChanges),
	};
}

/**
 * The end card's link: the hero's own call to action, which front-page.php
 * prints on the mount point from the same ACF field the hero button reads.
 * Null when the field is empty, and the figure then runs without a card.
 */
function readCta(host) {
	const label = host.dataset.ctaLabel;
	const href = host.dataset.ctaHref;
	return label && href ? { label, href } : null;
}

function mount() {
	const host = document.getElementById(MOUNT_ID);
	if (!host) return;

	const data = window.cdFigure ?? {};
	const base = typeof data.figuresUrl === "string" ? data.figuresUrl : "/figures";
	const cta = readCta(host);

	/* The placeholder PHP rendered is replaced, not appended to: it is there so
	   the section has its height before the bundle arrives, and leaving it
	   under the figure would show the same thing twice. */
	host.textContent = "";
	host.removeAttribute("data-placeholder");

	createRoot(host).render(
		<StringsProvider value={injectedStrings()}>
			<RevisionAnimation figure={withBase(figure, base)} cta={cta} />
		</StringsProvider>,
	);
}

if (document.readyState === "loading") {
	document.addEventListener("DOMContentLoaded", mount, { once: true });
} else {
	mount();
}
